import {
  Avatar,
  Box,
  type BoxProps,
  Button,
  FormControl,
  type FormControlProps,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { type FC, type ReactNode, useEffect, useRef, useState } from "react";
import {
  type Accept,
  ErrorCode,
  type FileError,
  type FileRejection,
  useDropzone,
} from "react-dropzone";
import toast from "react-hot-toast";
import { Duplicate } from "../../../../styles/icons/duplicate";
import { X } from "../../../../styles/icons/x";
import { getAspectRatio } from "../../../../utils/aspect-ratio";
import { bytesToSize } from "../../../../utils/bytes-to-size";
import { type ElementTracking, useElementTracking } from "../../../analytics/analytics";
import { FileIconWithChip } from "../icon-with-chip";

type Preview = { type: "image" | "video"; url: string } | { type: "file"; fileType: string | null };
type State = {
  uploading: boolean;
  imageUrl: string | null;
  preview: Preview | null;
  file?: File;
};

type TextConfig = {
  add?: string;
  remove: string;
  uploadError: string;
};

interface FilePickerProps {
  value: string | null;
  handleUpload: (file: File) => Promise<File | undefined>;
  accept: Accept;
  text: TextConfig;
  content?: ReactNode;
  disabled?: boolean;
  transform?: (file: File) => Promise<File>;
  handleRemove?: () => void;
  preview?: boolean;
  onClick?: (src?: string) => void;
  formControlProps?: FormControlProps;
  selected?: boolean;
  name?: string;
  fileTypeError?: () => void;
  minWidth?: BoxProps["minWidth"];
  successPreviewComponent?: ReactNode;
  extraButtons?: ReactNode;
}

export const FilePicker: FC<FilePickerProps & ElementTracking> = ({
  value,
  handleUpload,
  disabled,
  accept,
  text,
  content,
  transform,
  handleRemove,
  preview = true,
  onClick,
  formControlProps,
  selected = false,
  analyticsCategory,
  name,
  fileTypeError,
  minWidth = "400px",
  extraButtons,
}) => {
  const ref = useRef(null);
  const analyticsProps = useElementTracking(analyticsCategory, name, "file-picker");
  const [state, setState] = useState<State>({
    imageUrl: value,
    uploading: false,
    preview: null,
  });

  const [previewAspect, setPreviewAspect] = useState<null | number>(null);
  const [openImagePreview, setOpenImagePreview] = useState<boolean>(false);

  const maxFileSize = 550 * 1024 * 1024; // 550 MB
  const maxVideoSize = 5 * 1024 * 1024 * 1024; // 5 GB
  const sizeValidator = (file: File): FileError | null => {
    if (file.type.startsWith("video/") && file.size > maxVideoSize) {
      return {
        code: ErrorCode.FileTooLarge,
        message: "File is too large. Maximum size for videos is 5 GB.",
      };
    } else if (file.size > maxFileSize) {
      return {
        code: ErrorCode.FileTooLarge,
        message: "File is too large. Maximum size is 550 MB.",
      };
    }

    return null;
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept,
    maxFiles: 1,
    validator: sizeValidator,
    onDrop: (files: File[]): void => {
      if (files.length === 1) {
        const transformer = transform || (() => Promise.resolve(files[0]!));
        void transformer(files[0]!)
          .then((file) => {
            setState((prev) => ({
              ...prev,
              file,
              preview: previewType(file),
            }));
          })
          .catch(console.error);
      }
    },
    onDropRejected: (fileRejections: FileRejection[]): void => {
      const allErrors = new Set(fileRejections.flatMap((rejection) => rejection.errors));

      allErrors.forEach((error) => {
        if ((error.code as ErrorCode) === ErrorCode.TooManyFiles) {
          toast.error(text.uploadError);
        } else if ((error.code as ErrorCode) === ErrorCode.FileInvalidType) {
          fileTypeError?.();
        } else if ((error.code as ErrorCode) === ErrorCode.FileTooLarge) {
          toast.error(error.message);
        }
      });
    },
  });

  useEffect(() => {
    const setAspect = async (): Promise<void> => {
      if (!state.preview) return setPreviewAspect(null);
      // Will update this when we have a better way to handle PDF/video previews,
      // for now we just want it to be truthy to show the IconWithChip
      if (state.preview.type === "file" || state.preview.type === "video")
        return setPreviewAspect(1);

      setPreviewAspect(await getAspectRatio(state.preview.url));
    };
    void setAspect().catch(console.error);
  }, [state.preview]);

  useEffect(() => {
    void (async () => {
      if (!state.file) return;

      setState((prev) => ({ ...prev, uploading: true }));
      const file = await handleUpload(state.file);
      setState((prev) => ({
        ...prev,
        uploading: false,
        file,
        preview: file ? prev.preview : null,
      }));
    })().catch(console.error);
  }, [state.file, handleUpload, handleRemove]);

  const handleClick = (): void => {
    if (preview) {
      setOpenImagePreview(true);
    } else {
      state.preview?.type === "image" && onClick?.(state.preview.url);
    }
  };

  return (
    <FormControl variant="standard" sx={{ my: 1 }} fullWidth {...formControlProps}>
      {value && !state.preview && !disabled ? (
        <Box
          sx={{
            alignItems: "center",
            border: 1,
            borderRadius: 1,
            borderColor: "divider",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            p: 2,
            my: 1,
            minWidth,
          }}
        >
          <img
            src={value}
            alt="Selected"
            style={{
              maxWidth: "100%",
              maxHeight: "200px",
              objectFit: "contain",
              borderRadius: "4px",
            }}
          />
          {handleRemove && (
            <Stack direction="row" gap={1}>
              <Button
                onClick={handleRemove}
                variant="outlined"
                size="small"
                sx={{ mt: 2 }}
                data-analytics-id="remove-uploaded-logo"
              >
                {text.remove}
              </Button>
              {extraButtons}
            </Stack>
          )}
        </Box>
      ) : !state.preview && !disabled ? (
        <Box
          sx={{
            alignItems: "center",
            border: 1,
            borderRadius: 1,
            borderStyle: "dashed",
            borderColor: "divider",
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            outline: "none",
            px: 3,
            my: 1,
            ...(isDragActive && {
              backgroundColor: "action.active",
              opacity: 0.5,
            }),
            "&:hover": {
              backgroundColor: "action.hover",
              cursor: "pointer",
              opacity: 0.5,
            },
            width: "100",
            minWidth,
          }}
          data-testid="file-dropzone"
          {...getRootProps()}
        >
          <input {...analyticsProps} {...getInputProps()} disabled={disabled} />
          {content ? (
            content
          ) : (
            <>
              <Box
                sx={{
                  "& img": {
                    width: 100,
                  },
                }}
              >
                {<Avatar sx={{ bgcolor: "success.main" }}>+</Avatar>}
              </Box>
              <Box sx={{ p: 2 }}>
                <Typography variant="button">{text.add}</Typography>
              </Box>
            </>
          )}
        </Box>
      ) : null}

      {previewAspect && (
        <Box ref={ref} minWidth={minWidth}>
          <List sx={{ width: "100%", p: 0 }}>
            <ListItem
              key={state.file?.name ?? ""}
              sx={{
                border: 2,
                borderColor: "divider",
                borderRadius: 1,
                "& + &": {
                  mt: 1,
                },
                paddingX: 1,
                width: "100%",
                display: "block",
                ...(selected && {
                  border: 2,
                  borderStyle: "solid",
                  borderColor: "primary.main",
                }),
              }}
            >
              <ImagePreview state={state} onClick={handleClick} />
              <Tooltip title={text.remove}>
                <IconButton
                  data-analytics-id="file-picker-dialog-remove"
                  sx={{ position: "absolute", top: 0, right: 0 }}
                  disabled={disabled}
                  onClick={() => {
                    setPreviewAspect(null);
                    setState((prev) => ({ ...prev, file: undefined, preview: null }));
                    handleRemove?.();
                  }}
                >
                  <X data-testid="XIcon" fontSize="small" />
                </IconButton>
              </Tooltip>
            </ListItem>
          </List>

          {preview && state.preview?.type === "image" && (
            <Popover
              // eslint-disable-next-line react-hooks/refs
              anchorEl={ref.current}
              anchorOrigin={{
                vertical: "center",
                horizontal: "center",
              }}
              transformOrigin={{
                vertical: "center",
                horizontal: "right",
              }}
              open={openImagePreview}
              onClose={() => setOpenImagePreview(false)}
            >
              <img src={state.preview.url} alt="preview" width={500} />
            </Popover>
          )}
        </Box>
      )}
    </FormControl>
  );
};

const ImagePreview: FC<{
  state: State;
  onClick: () => void;
}> = ({ state, onClick }) => {
  if (state.uploading) {
    return (
      <Box display="flex">
        <ListItemIcon>
          <Button data-analytics-id="file-picker-dialog-loading" loading={true} />
        </ListItemIcon>
        <ListItemText
          primary={
            <Button data-analytics-id="file-picker-dialog-uploading" disabled={true}>
              Uploading...
            </Button>
          }
          slotProps={{
            primary: {
              color: "textPrimary",
              variant: "subtitle2",
            },
          }}
        />
      </Box>
    );
  }

  let previewComponent;
  if (state.preview?.type === "file") {
    previewComponent = <FileIconWithChip fileType={state.preview.fileType?.toUpperCase()} />;
  } else if (state.preview?.type === "video") {
    previewComponent = <FileIconWithChip fileType="video" />;
  } else {
    previewComponent = (
      <IconButton
        data-analytics-id="file-picker-dialog-preview"
        onClick={onClick}
        sx={{ width: "100%" }}
      >
        <img src={state.preview?.url || ""} alt="preview" style={{ width: "100%" }} />
      </IconButton>
    );
  }

  return (
    <Stack display="flex" alignItems="center" justifyContent="center">
      <ListItemIcon>
        {state.preview ? previewComponent : <Duplicate fontSize="small" />}
      </ListItemIcon>
      {state.file?.size && (
        <ListItemText
          secondary={<Box component="span">{state.file.size && bytesToSize(state.file.size)}</Box>}
        />
      )}
    </Stack>
  );
};

const previewType = (file: File): Preview => {
  if (file.type.startsWith("image/")) {
    return { type: "image", url: URL.createObjectURL(file) };
  }

  if (file.type.startsWith("video/")) {
    return { type: "video", url: URL.createObjectURL(file) };
  }

  const formatMatch = file.type.match(/(?:font-|font\/)(\w+)$/);
  const fileType = formatMatch && formatMatch[1] ? formatMatch[1].toLowerCase() : null;

  return { type: "file", fileType };
};
